import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { buildUploadFileName, buildUploadZipFileName, formatUploadDisplayName } from '../../utils/functions';
import {
  isAttachmentUploadType,
  uploadBatchStatusForType,
  sftpUploadStatusForType,
  isAllowedExtensionForType,
  DROP_ACCEPT,
  DROP_EXTENSIONS,
} from '../../utils/uploadBatchType';
import { 
  Upload, 
  Button, 
  Card, 
  Tag,
  List, 
  Progress, 
  Typography, 
  Space, 
  Badge,
  Divider,
  message,
  Radio,
  notification,
  Modal,
  Form,
  Alert,
  Select
} from 'antd';
import { 
  UploadOutlined, 
  FolderOpenOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { RefreshIcon } from '../common/ActionIcons';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import FileList from './common/FileList.component';
import UploadSessionManager from '../cohort/UploadSessionManager';
import QuickPatientSelect from '../patient/QuickPatientSelect.component';
import { FaTimesCircle, FaRedo, FaFilePdf, FaFile } from 'react-icons/fa';
import { FaFileZipper } from 'react-icons/fa6';
import { IoCloudOfflineSharp } from "react-icons/io5";
import { createUploadBatch } from '../../actions/uploadBatchActions';

const { Text } = Typography;

// Constants
const UPLOAD_CARD_STYLE = {
  border: '2px dashed #d9d9d9',
  borderRadius: 8,
  textAlign: 'center',
  marginBottom: 24
};

const UPLOAD_AREA_STYLE = {
  padding: '14px 14px'
};

const PROGRESS_CONTAINER_STYLE = {
  marginTop: 24,
  textAlign: 'left',
  maxWidth: 500,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%'
};

const PROGRESS_ITEM_STYLE = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
  gap: 8,
  minWidth: 0
};

const FILE_LIST_CONTAINER_STYLE = {
  maxHeight: 500,
  overflowY: 'auto',
  overflowX: 'hidden',
  minWidth: 0
};

const MAX_FILE_SIZE_MB = 200;

const UPLOAD_TYPES = [
  { value: 'CARTO', label: 'CARTO' },
  { value: 'EnSiteX', label: 'EnSiteX' },
  { value: 'AUTO', label: 'AUTO (detect automatically)' },
  { value: 'PDF', label: 'PDF' },
  { value: 'DICOM', label: 'DICOM' },
];

const FileUploader = ({ 
  onChange = () => {}, 
  category = '', 
  ownerId = '', 
  ownerName = '',
  browsingMode = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subDirs, setSubDirs] = useState([]);
  const [currentSubDir, setCurrentSubDir] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [status, setStatus] = useState('pending');
  const [totalSize, setTotalSize] = useState({ pending: '0 B', processed: '0 B' });
  const [cohortModalVisible, setCohortModalVisible] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPatientIdentifier, setSelectedPatientIdentifier] = useState('');
  const [form] = Form.useForm();
  const selectedUploadType = Form.useWatch('uploadType', form);
  const [dataProcessingPermitted, setDataProcessingPermitted] = useState(false);
  const [fileListError, setFileListError] = useState(null);

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  // Redux state for upload batch creation - following DynamicFormSP pattern
  const uploadBatchCreate = useSelector(state => state.uploadBatchCreate);
  const { loading: loadingCreate, error: errorCreate, success: successCreate, uploadBatch: createdUploadBatch } = uploadBatchCreate;

  // Reset Redux state on component mount and unmount - following DynamicFormSP pattern
  useEffect(() => {
    dispatch({ type: 'UPLOAD_BATCH_CREATE_RESET' });
  }, [dispatch]);

  // Show success notification on successCreate - following DynamicFormSP pattern
  useEffect(() => {
    if (successCreate && createdUploadBatch) {
      openNotification('bottom', t('Success'), t('Upload batch created successfully'));
      // Don't reset immediately - let user see the success button
    }
  }, [successCreate, createdUploadBatch]);

  // Show error notification on errorCreate - following DynamicFormSP pattern
  useEffect(() => {
    if (errorCreate) {
      const errorMessage = Array.isArray(errorCreate) ? errorCreate.join(', ') : errorCreate;
      openNotification('bottom', t('Error'), t('Failed to create upload batch: {{error}}', { error: errorMessage }));
      dispatch({ type: 'UPLOAD_BATCH_CREATE_RESET' });
    }
  }, [errorCreate]);

  // listen to dsaStatus, dpaStatus, tocStatus in userInfo
  useEffect(() => {
    if (userInfo?.dsaStatus === 'Signed' && userInfo?.dpaStatus === 'Signed' && userInfo?.tocStatus === 'Signed') {
      setDataProcessingPermitted(true);
    }
  }, [userInfo?.dsaStatus, userInfo?.dpaStatus, userInfo?.tocStatus]);

  // Show notification - following DynamicFormSP pattern
  const [api, contextHolder] = notification.useNotification();
  const openNotification = (placement, type, description) => {
    api.info({
      message: type,
      description: description,
      placement,
      icon: type === t('Success') ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#eb2f96" />,
    });
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to determine file format
  const getFileFormat = (fileName) => {
    const extension = String(fileName || '').toLowerCase().split('.').pop();
    if (extension === 'zip') return 'ZIP';
    if (extension === 'pdf') return 'PDF';
    if (extension === 'dcm' || extension === 'dicom') return 'DICOM';
    return extension ? extension.toUpperCase() : 'Unknown';
  };

  // Helper function to generate file hash (simple implementation)
  const generateFileHash = (file) => {
    return `sha256:${file.name}_${file.size}_${Date.now()}`;
  };

  // Helper function to create upload batch data (uploadType and patientId are required, no default)
  const createUploadBatchData = (file, uploadResponse, cohortId, uploadType, patientId) => {
    const now = new Date().toISOString();
    
    // Get organisationId from the correct path in userInfo
    const organisationId = userInfo?.applicationUserProfile?.businessOrganisationId || 
                          userInfo?.businessOrganisationId ||
                          userInfo?.organizationId ||
                          userInfo?.organisation?.id ||
                          userInfo?.organization?.id;
    
    
    if (!organisationId) {
      throw new Error('Organisation ID not found in user profile');
    }

    if (!cohortId) {
      throw new Error('Cohort ID is required for upload batch');
    }

    if (!uploadType || typeof uploadType !== 'string' || !uploadType.trim()) {
      throw new Error('Upload type is required');
    }

    if (!patientId) {
      throw new Error('Patient is required for upload batch');
    }
    
    return {
      organisationId: organisationId,
      cohortId: cohortId,
      patientId: patientId,
      fileName: file.name,
      filePath: uploadResponse?.filePath || uploadResponse?.path || uploadResponse?.fullPath || file.name,
      fileSize: file.size,
      fileFormat: getFileFormat(file.name),
      fileHash: generateFileHash(file),
      uploadDateTime: now,
      uploadMethod: 'Web',
      uploadedBy: userInfo?.email || userInfo?.userName || 'Unknown',
      status: uploadBatchStatusForType(uploadType),
      totalRecords: 0,
      processedRecords: 0,
      failedRecords: 0,
      validRecords: 0,
      isFormatValid: isAttachmentUploadType(uploadType),
      validationErrors: null,
      validationCompleted: isAttachmentUploadType(uploadType) ? now : null,
      customerPrefix: userInfo?.applicationUserProfile?.businessOrganisation?.name?.toLowerCase().replace(/\s+/g, '_') || 
                     userInfo?.organization?.name?.toLowerCase().replace(/\s+/g, '_') || 
                     'default',
      isPrefixed: false,
      type: uploadType.trim(),
      processingJobIds: []
    };
  };

  const getFileIcon = (file) => {
    const name = (file?.name || '').toLowerCase();
    const iconStyle = { fontSize: 16 };
    if (name.endsWith('.pdf')) return <FaFilePdf style={{ ...iconStyle, color: '#f5222d' }} />;
    if (name.endsWith('.dcm') || name.endsWith('.dicom')) return <FaFile style={{ ...iconStyle, color: '#722ed1' }} />;
    return <FaFileZipper style={{ ...iconStyle, color: '#1890ff' }} />;
  };

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      processing: { color: 'processing', text: t('Processing') },
      pending: { color: 'warning', text: t('Pending') },
      completed: { color: 'success', text: t('Completed') },
      error: { color: 'error', text: t('Error') }
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return <Badge status={config.color} text={config.text} />;
  };

  const isValidFileType = (fileName) => {
    const lower = String(fileName || '').toLowerCase();
    return DROP_EXTENSIONS.some((ext) => lower.endsWith(ext));
  };

  const fetchFiles = async (subDir = currentSubDir, statusArg = status) => {
    if (!userInfo?.token) return;
    setLoading(true);
    setFileListError(null);
    try {
      const params = new URLSearchParams({
        limit: subDir ? '100' : '20',
        sortBy: 'lastModified',
        sortOrder: 'desc',
        status: statusArg,
      });
      if (subDir) params.append('subDir', subDir);
      if (ownerId) params.append('ownerId', ownerId);
      
      const response = await axios.get(`/dotnet_api/v1/storage/sftp-list?${params}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` }
      });
      
      const files = (response.data.files || []).map(file => ({
        uid: file.id || file.fullPath,
        name: file.name,
        status: 'done',
        size: file.size,
        sizeFormatted: file.sizeFormatted || formatFileSize(file.size || 0),
        type: file.type,
        path: file.fullPath,
        lastModified: file.lastModified,
        ...file
      }));
      
      setFileList(files);
      setSubDirs(response.data.subDirs || []);
      const totals = response.data.totalSize;
      setTotalSize({
        pending: formatFileSize(Number(totals?.pending) || 0),
        processed: formatFileSize(Number(totals?.processed) || 0),
      });
      
      if (!currentSubDir && response.data.subDirs && response.data.subDirs.length > 0) {
        setCurrentSubDir(response.data.subDirs[0]);
      }
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setFileListError(error);
    } finally {
      setLoading(false);
    }
  };

  const retryUpload = (fileObj) => {
    if (!fileObj || !fileObj.file) return;
    customRequest({
      file: fileObj.file,
      onSuccess: fileObj.onSuccess,
      onError: fileObj.onError,
      onProgress: fileObj.onProgress
    });
    setUploadingFiles(prev => prev.filter(f => f.uid !== fileObj.uid));
  };

  const customRequest = async ({ file, onSuccess, onError, onProgress }) => {
    // Store file info and show cohort selection modal
    setPendingFiles(prev => [...prev, { file, onSuccess, onError, onProgress }]);
    setCohortModalVisible(true);
    return;
  };

  const processPendingFiles = async () => {
    try {
      await form.validateFields();
    } catch (err) {
      return;
    }

    const uploadType = form.getFieldValue('uploadType');
    if (!uploadType || !String(uploadType).trim()) {
      message.error(t('Please select an upload type'));
      return;
    }

    if (!selectedCohortId) {
      message.error(t('Please select an upload session before uploading files'));
      return;
    }

    if (!selectedPatientId || !selectedPatientIdentifier) {
      message.error(t('Please select a patient before uploading files'));
      return;
    }

    const filesToProcess = [...pendingFiles];
    setPendingFiles([]);
    setCohortModalVisible(false);

    for (const { file, onSuccess, onError, onProgress } of filesToProcess) {
      if (!isAllowedExtensionForType(file.name, uploadType)) {
        message.error(t('{{name}} is not valid for type {{type}}', { name: file.name, type: uploadType }));
        onError?.(new Error('invalid type'));
        continue;
      }

      setUploadingFiles(prev => [
        ...prev,
        {
          uid: file.uid,
          name: file.name,
          percent: 0,
          status: 'uploading',
          file,
          onSuccess,
          onError,
          onProgress
        }
      ]);

      const renamedFile = new File(
        [file],
        isAttachmentUploadType(uploadType)
          ? buildUploadFileName(file.name, selectedPatientIdentifier)
          : buildUploadZipFileName(file.name, selectedPatientIdentifier),
        { type: file.type }
      );

      const formData = new FormData();
      formData.append('file', renamedFile);
      formData.append('type', uploadType);
      formData.append('ownerId', ownerId);
      formData.append('ownerName', ownerName);
    
      try {
        const sftpStatus = sftpUploadStatusForType(uploadType);
        const response = await axios.post(`/dotnet_api/v1/storage/sftp-upload?status=${sftpStatus}`, formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${userInfo?.token}`
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadingFiles(prev => prev.map(f => f.uid === file.uid ? { ...f, percent } : f));
            onProgress({ percent });
          }
        });
        
        setUploadingFiles(prev => prev.map(f => f.uid === file.uid ? { ...f, percent: 100, status: 'success' } : f));
        onSuccess(response.data, file);
        
        // Create upload batch when file upload is successful - following DynamicFormSP pattern
        try {
          const uploadBatchData = createUploadBatchData(renamedFile, response.data, selectedCohortId, uploadType, selectedPatientId);
          dispatch(createUploadBatch(uploadBatchData));
        } catch (batchError) {
          console.error('Failed to prepare upload batch data:', batchError);
          message.warning(t('File uploaded but failed to prepare batch data: {{error}}', { error: batchError.message }));
        }
        
        fetchFiles();
      } catch (error) {
        setUploadingFiles(prev => prev.map(f => f.uid === file.uid ? { ...f, status: 'error' } : f));
        onError(error);
        message.error(t('Upload failed: {{error}}', { error: error.message }));
      }
    }
  };

  const handleRemove = async (file) => {
    try {
      await axios.delete(`/dotnet_api/v1/storage/sftp-delete`, {
        headers: { Authorization: `Bearer ${userInfo?.token}` },
        data: { 
          filePath: file.path || file.fullPath,
          fileName: file.name 
        }
      });
      
      message.success(t('File deleted successfully'));
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
      fetchFiles();
      
    } catch (error) {
      console.error('Failed to remove file:', error);
      setFileListError(error);
    }
  };

  const handleDownload = async (file) => {
    try {
      const url = `/dotnet_api/v1/storage/sftp-download?filePath=${encodeURIComponent(file.fullPath)}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${userInfo?.token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: file.type || 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = formatUploadDisplayName(file.name) || file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setFileListError(error);
    }
  };

  const handleSubDirChange = (dir) => {
    setCurrentSubDir(dir);
    fetchFiles(dir);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    fetchFiles(currentSubDir, e.target.value);
  };

  const handleCohortModalCancel = () => {
    setCohortModalVisible(false);
    setPendingFiles([]);
    setSelectedCohortId(null);
    setSelectedPatientId(null);
    setSelectedPatientIdentifier('');
    form.resetFields();
  };

  const handlePatientSelect = (patientId, option) => {
    setSelectedPatientId(patientId);
    setSelectedPatientIdentifier(option?.patientIdentifier || '');
  };

  const handleCohortSelect = (cohortId) => {
    setSelectedCohortId(cohortId);
  };

  const uploadProps = {
    customRequest: dataProcessingPermitted ? customRequest : () => {},
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
      setUploading(newFileList.some(file => file.status === 'uploading'));
    },
    onRemove: handleRemove,
    beforeUpload: (file) => {
      if (!dataProcessingPermitted) {
        message.error(t('Data processing is not permitted. Please complete all required agreements.'));
        return false;
      }
      
      if (!isValidFileType(file.name)) {
        message.error(t('Allowed files: ZIP (studies), PDF, or DICOM (.dcm / .dicom)'));
        return false;
      }
      
      const isLt200M = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB;
      if (!isLt200M) {
        message.error(t('File must be smaller than 200MB!'));
        return false;
      }
      
      return true;
    },
    multiple: true,
    maxCount: 10,
    accept: DROP_ACCEPT,
    disabled: !dataProcessingPermitted
  };

  useEffect(() => {
    if (userInfo?.token) {
      fetchFiles();
    }
  }, [userInfo?.token]);

  // Calculate successful files count for success message
  const successfulFilesCount = uploadingFiles.filter(f => f.status === 'success').length;
  const fileCountText = successfulFilesCount === 1 
    ? t('{{count}} file uploaded successfully!', { count: successfulFilesCount })
    : t('{{count}} files uploaded successfully!', { count: successfulFilesCount });

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {contextHolder}
      {!browsingMode && (
        <Card style={UPLOAD_CARD_STYLE}>
          <Upload.Dragger
            {...uploadProps}
            style={{ border: 'none' }}
            fileList={[]}
            showUploadList={false}
            onChange={() => {}}
          >
                          {dataProcessingPermitted ? (
            <div style={UPLOAD_AREA_STYLE}>
              <UploadOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
              
              <div>
                <Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                  {t('Click or drag files to upload')}
                </Text>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {t('Supports CARTO/EnSiteX ZIPs, PDF, and DICOM (.dcm), up to 200MB')}
                </Text>
              </div>
            </div>
          ) : (
            <div style={UPLOAD_AREA_STYLE}>
              <IoCloudOfflineSharp style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
              
              <div>
                <Text style={{ fontSize: 16, display: 'block', marginBottom: 8, color: '#ff4d4f' }}>
                  {t('Upload Disabled')}
                </Text>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {t('Data processing is not permitted. Please complete all required agreements (DSA, DPA, TOC) to enable file uploads.')}
                </Text>
              </div>
            </div>
          )}
            {uploadingFiles.length > 0 && (
              <div style={PROGRESS_CONTAINER_STYLE}>
                <List
                  size="small"
                  dataSource={uploadingFiles}
                  renderItem={f => (
                    <List.Item style={{ padding: 0 }}>
                      <div style={PROGRESS_ITEM_STYLE}>
                        <span style={{ flex: '1 1 120px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Text ellipsis style={{ width: '100%' }}>
                            {f.name} ({formatFileSize(f.file?.size || 0)})
                          </Text>
                        </span>
                        <span style={{ flex: '0 0 120px', minWidth: 80, maxWidth: 160 }}>
                          <Progress percent={f.percent} size="small" style={{ width: '100%' }} showInfo={f.percent === 100} />
                        </span>
                        {f.status === 'error' && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <FaTimesCircle style={{ color: '#ff4d4f', fontSize: 18 }} title={t('Upload failed')} />
                            <Button
                              type="link"
                              size="small"
                              icon={<FaRedo />}
                              onClick={() => retryUpload(f)}
                              style={{ padding: 0 }}
                            >
                              {t('Retry')}
                            </Button>
                          </span>
                        )}
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Upload.Dragger>
          
          {/* Success Alert - Show when upload batch is created successfully */}
          {successCreate && createdUploadBatch && (
            <Alert
              message={fileCountText}
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              action={
                <Button
                  type="primary"
                  size="small"
                  icon={<ArrowRightOutlined />}
                  onClick={() => {
                    dispatch({ type: 'UPLOAD_BATCH_CREATE_RESET' });
                    navigate('/upload_batches');
                  }}
                >
                  {t('Go to Upload Batches')}
                </Button>
              }
              style={{ marginTop: 16 }}
            />
          )}
        </Card>
      )}

      <Card
        style={{ overflow: 'hidden' }}
        title={
          <Space>
            <FolderOpenOutlined />
            <Text strong>
              {browsingMode ? t('Recent Files') : t('Uploaded Files')} ({fileList.length})
            </Text>
          </Space>
        }
        extra={
          <Space>
            <Button 
              type="text" 
              size="small" 
              icon={<RefreshIcon />}
              onClick={() => fetchFiles(currentSubDir)}
              loading={loading}
            >
              {t('Refresh')}
            </Button>
          </Space>
        }
        actions={[
          <Radio.Group value={status} onChange={handleStatusChange} buttonStyle="solid" size="small">
            <Radio.Button value="pending">{t('Pending')} ({totalSize.pending})</Radio.Button>
            <Radio.Button value="processed">{t('Processed')} ({totalSize.processed})</Radio.Button>
          </Radio.Group>
        ]}
        loading={loading}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 24, flexWrap: 'wrap' }}>
          <Text type="secondary">
            {t('Selected date')}: <b>{currentSubDir || t('—')}</b>
          </Text>
        </div>
        
        <div style={FILE_LIST_CONTAINER_STYLE}>
          <FileList
            files={fileList}
            loading={loading}
            onDelete={handleRemove}
            onDownload={handleDownload}
            subDirs={subDirs}
            currentSubDir={currentSubDir}
            onSubDirChange={handleSubDirChange}
            subDirLabel="Date"
            t={t}
            status={status}
            error={fileListError}
            onClearError={() => setFileListError(null)}
          />
        </div>
      </Card>

      {jobs.length > 0 && (
        <>
          <Divider />
          <Card 
            title={<Text strong>{t('Processing Jobs')}</Text>}
            size="small"
            bodyStyle={{ padding: 0 }}
          >
            <List
              size="small"
              dataSource={jobs}
              renderItem={(job) => (
                <List.Item style={{ padding: '8px 16px' }}>
                  <List.Item.Meta
                    avatar={getFileIcon({ name: job.fileName, type: job.fileType })}
                    title={job.fileName}
                    description={<StatusBadge status={job.status} />}
                  />
                </List.Item>
              )}
            />
          </Card>
        </>
      )}

      {/* Upload session and type selection modal */}
      <Modal
        title={t('Select Patient, Session and Type for Upload')}
        open={cohortModalVisible}
        onOk={processPendingFiles}
        onCancel={handleCohortModalCancel}
        width={600}
        okButtonProps={{ disabled: !selectedCohortId || !selectedUploadType || !selectedPatientId }}
        okText={t('Upload Files')}
        cancelText={t('Cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ uploadType: undefined }}>
          <Form.Item
            label={
              <>
                {t('Patient')} <Text type="danger">*</Text>
              </>
            }
            name="patientId"
            rules={[{ required: true, message: t('Please select or create a patient') }]}
          >
            <QuickPatientSelect
              onChange={handlePatientSelect}
              value={selectedPatientId}
            />
          </Form.Item>
          <Form.Item
            label={
              <>
                {t('Type')} <Text type="danger">*</Text>
              </>
            }
            name="uploadType"
            rules={[{ required: true, message: t('Please select a type') }]}
          >
            <Select
              placeholder={t('Select upload type')}
              options={UPLOAD_TYPES}
              size="large"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label={
              <>
                {t('Upload Session')} <Text type="danger">*</Text>
              </>
            }
            name="cohortId"
            rules={[{ required: true, message: t('Please select an upload session') }]}
          >
            <UploadSessionManager
              onChange={handleCohortSelect}
              value={selectedCohortId}
            />
          </Form.Item>
          <Typography.Paragraph type="secondary">
            {isAttachmentUploadType(selectedUploadType)
              ? t('PDF and DICOM files are stored as-is and the upload batch is marked Completed. NiFi is not involved.')
              : t('The zip will be renamed to originalfilename-patientidentifier.zip and NiFi will validate, convert to .mat, and attach it to this patient\'s upload batch automatically.')}
          </Typography.Paragraph>
          {pendingFiles.length > 0 && (
            <div>
              <Typography.Text strong>{t('Files to upload:')}</Typography.Text>
              <List
                size="small"
                dataSource={pendingFiles}
                renderItem={({ file }) => (
                  <List.Item>
                    {getFileIcon(file)} {file.name} ({formatFileSize(file.size || 0)})
                  </List.Item>
                )}
              />
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default FileUploader;